import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const DOCS = [
  ["privacy", "privacy_text", "Política de privacidad", "/privacidad"],
  ["notice", "notice_text", "Aviso legal", "/aviso-legal"],
];

// Política de privacidad y aviso legal de la página de reservas (backend/legal.py). Sin texto
// propio se publica el texto por defecto con los datos del perfil de empresa.
export default function LegalSettings() {
  const [d, setD] = useState(null);
  const [own, setOwn] = useState({ privacy: false, notice: false });
  const [saving, setSaving] = useState(false);
  // Supresión automática activa (Protección de datos): una política propia tiene que decir sus plazos.
  const [conservacion, setConservacion] = useState(false);

  const load = (data) => {
    setD(data);
    setOwn({ privacy: Boolean(data.privacy_text), notice: Boolean(data.notice_text) });
  };

  useEffect(() => {
    api.get("/admin/legal").then(({ data }) => load(data)).catch((e) => toast.error(formatError(e)));
    // Es un recordatorio: si no se puede leer, la pantalla sigue sin él.
    api.get("/admin/privacy/settings").then(({ data }) => setConservacion(data?.enabled === true)).catch(() => {});
  }, []);

  const setText = (field) => (e) => setD((x) => ({ ...x, [field]: e.target.value }));
  const usarPropio = (doc, field, propio) => {
    setOwn((o) => ({ ...o, [doc]: propio }));
    // Se parte del texto por defecto para no empezar de cero.
    if (propio && !d[field]) setD((x) => ({ ...x, [field]: x.defaults[doc].es }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/legal", {
        privacy_text: own.privacy ? d.privacy_text : "",
        notice_text: own.notice ? d.notice_text : "",
      });
      load(data);
      toast.success("Textos legales guardados");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <header className="bg-white border-b border-[#DCD8D0]">
        <div className="container-x py-4">
          <Link to="/admin" className="inline-flex items-center text-[#5C5C5C] hover:text-marca"><ArrowLeft className="h-4 w-4 mr-2" /> Panel</Link>
        </div>
      </header>
      <main className="container-x py-8 max-w-4xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#1A1A1A]">Privacidad y aviso legal</h1>
          <Button onClick={save} disabled={saving || !d} className="bg-marca hover:bg-marca-hover text-white" data-testid="legal-save">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
        <p className="text-sm text-[#5C5C5C]">
          Tus clientes ven estos textos en la página de reservas, enlazados desde el pie, desde la casilla de
          aceptación de la reserva y desde el pre-checkin. Si no escribes uno propio, se publica el texto por
          defecto con los datos de tu <Link to="/admin/empresa" className="underline">perfil de empresa</Link>{" "}
          (razón social, NIF, domicilio, correo y teléfono), en castellano y en catalán. Los plazos de conservación se
          ajustan en <Link to="/admin/privacidad" className="underline">Protección de datos</Link>.
        </p>
        <div className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-4 text-sm text-[#7A4E0E]" data-testid="legal-draft-warning">
          El texto por defecto es un modelo pendiente de revisión legal. Revísalo con tu abogado antes de vender y
          escribe el tuyo si tu empresa trata otros datos, tiene otros plazos de conservación o está inscrita en el
          Registro Mercantil (el aviso legal tiene que llevar esos datos).
        </div>
        {d && DOCS.map(([doc, field, titulo, ruta]) => (
          <section key={doc} className="bg-white border border-[#DCD8D0] rounded-xl p-6 space-y-3" data-testid={`legal-${doc}-section`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-lg font-semibold text-[#1A1A1A]">{titulo}</h2>
              <a href={ruta} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-marca underline">
                Ver la página <ExternalLink className="h-3.5 w-3.5 ml-1" />
              </a>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input type="radio" name={`legal-${doc}`} checked={!own[doc]} onChange={() => usarPropio(doc, field, false)} data-testid={`legal-${doc}-default`} />
                Texto por defecto
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name={`legal-${doc}`} checked={own[doc]} onChange={() => usarPropio(doc, field, true)} data-testid={`legal-${doc}-own`} />
                Mi propio texto (en todos los idiomas)
              </label>
            </div>
            {doc === "privacy" && own.privacy && conservacion && (
              <p role="status" className="rounded-xl border border-[#E89B33]/40 bg-[#E89B33]/10 p-3 text-sm text-[#7A4E0E]" data-testid="legal-privacy-retention">
                Tu política de privacidad es un texto propio: revisa que diga los plazos de conservación de{" "}
                <Link to="/admin/privacidad" className="underline">Protección de datos</Link>.
              </p>
            )}
            {own[doc] ? (
              <div>
                <Label htmlFor={`legal-${doc}-text`}>Texto</Label>
                <Textarea id={`legal-${doc}-text`} className="mt-1 bg-[#F7F5F0] border-[#DCD8D0] min-h-[360px] font-mono text-xs"
                  value={d[field]} onChange={setText(field)} data-testid={`legal-${doc}-text`} />
                <p className="text-xs text-[#8A8A8A] mt-2">
                  Una línea en blanco separa párrafos. <code>## Título</code> abre un apartado, las líneas que empiezan
                  por <code>- </code> forman una lista y <code>**texto**</code> sale en negrita. Marcadores:{" "}
                  {Object.keys(d.markers || {}).map((m) => <code key={m} className="mr-1">{`{{${m}}}`}</code>)}
                </p>
              </div>
            ) : (
              <details className="text-sm text-[#5C5C5C]">
                <summary className="cursor-pointer">Ver el texto por defecto</summary>
                <pre className="mt-2 whitespace-pre-wrap font-mono text-xs bg-[#F7F5F0] border border-[#DCD8D0] rounded-md p-3">{d.defaults[doc].es}</pre>
              </details>
            )}
          </section>
        ))}
      </main>
    </div>
  );
}
